<template>
  <v-menu
    v-model="dateRangeMenuOpen"
    min-width="auto"
    :disabled="disabled"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="formattedDateRange"
        append-icon="mdi-calendar"
        class="report-date-range-input"
        readonly
        outlined
        hide-details
        v-bind="attrs"
        v-on="on"
        :label="label"
        @click:append="dateRangeMenuOpen = true"
      />
    </template>

    <v-card
      class="date-picker-card"
      outlined
      rounded
    >
      <v-card-text class="pa-0">
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="4"
          >
            <v-list dense>
              <v-subheader class="pl-4">PRESETS</v-subheader>
              <v-list-item
                v-for="preset in presets"
                :key="preset.label"
                :class="{ 'active white--text': dateRange[0] === preset.range[0] && dateRange[1] === preset.range[1] }"
                @click="usePresetRange(preset)"
              >
                <v-list-item-title>{{ preset.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>

          <v-divider
            vertical
            class="d-none d-sm-block"
          />

          <v-col
            cols="12"
            sm="8"
            class="pl-sm-1"
          >
            <v-date-picker
              v-model="dateRange"
              range
              scrollable
              no-title
              full-width
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="dateRangeMenuOpen = false"
        >Close</v-btn>
        <v-btn
          class="btn-primary"
          @click="applyDateRange"
        >Apply</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DateRangePicker',
  props: ['selectedTimeRange', 'label', 'disabled'],
  data() {
    const today = moment();

    const lastWeekStart = moment().subtract(1, 'weeks').startOf('week');
    const lastWeekEnd = moment().subtract(1, 'weeks').endOf('week');

    const lastMonthStart = moment().subtract(1, 'month').startOf('month');
    const lastMonthEnd = moment().subtract(1, 'month').endOf('month');

    const lastYearStart = moment().subtract(1, 'year').startOf('year');
    const lastYearEnd = moment().subtract(1, 'year').endOf('year');

    const thisMonthStart = moment().startOf('month');

    return {
      dateRangeMenuOpen: false,
      dateRange: this.selectedTimeRange?.length
        ? this.selectedTimeRange
        : [this.formatDate(lastMonthStart), this.formatDate(lastMonthEnd)],
      presets: [
        {
          label: 'Last year',
          range: [this.formatDate(lastYearStart), this.formatDate(lastYearEnd)],
        },
        {
          label: 'Last month',
          range: [this.formatDate(lastMonthStart), this.formatDate(lastMonthEnd)],
        },
        {
          label: 'Last week',
          range: [this.formatDate(lastWeekStart), this.formatDate(lastWeekEnd)],
        },
        {
          label: 'MTD',
          range: [this.formatDate(thisMonthStart), this.formatDate(today)],
        },
      ],
    };
  },
  computed: {
    formattedDateRange() {
      if (!this.dateRange || this.dateRange.length < 2) {
        return '';
      }

      const startDate = this.dateRange[0];
      const endDate = this.dateRange[1];

      return `${this.formatDateForDisplay(startDate)} to ${this.formatDateForDisplay(endDate)}`;
    },
  },
  methods: {
    formatDate(date) {
      return date.format('YYYY-MM-DD');
    },
    formatDateForDisplay(dateStr) {
      const [year, month, day] = dateStr.split('-');
      return `${month}/${day}/${year}`;
    },
    emitRangeSelectEvent() {
      const [startDate, endDate] = this.dateRange;
      if (!startDate || !endDate) {
        console.warn('Date range not fully selected.');
        this.loading = false;
        return;
      }

      this.$emit('select-range', { startDate, endDate });
    },
    usePresetRange(preset) {
      this.dateRange = [...preset.range];
      this.dateRangeMenuOpen = false;
      this.emitRangeSelectEvent();
    },
    applyDateRange() {
      this.dateRangeMenuOpen = false;
      this.emitRangeSelectEvent();
    },
  },
  watch: {
    selectedTimeRange(newVal, oldVal) {
      if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) {
        return;
      }

      this.dateRange = newVal;
    },
  },

  mounted() {
    this.emitRangeSelectEvent();
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

.report-date-range-input {
  max-width: 400px;

  @media (max-width: $breakpoint-mobile) {
    max-width: 100%;
  }
}

.date-picker-card {
  margin-top: 8px;
  box-shadow: $shadow-lg !important;

  @media (max-width: $breakpoint-mobile) {
    max-width: 90vw;
  }

  ::v-deep .v-date-picker-table {
    height: auto !important;
    padding-bottom: 8px;

    .v-btn {
      margin: 2px 0;
    }
  }

  ::v-deep .v-list-item {
    cursor: pointer;
    border-radius: 6px;
    margin: 4px 8px;
    transition: background-color $transition-fast;

    &.active {
      background-color: #163958;
    }

    &:not(.active):hover {
      background-color: rgba(176, 211, 94, 0.1);
    }
  }

  ::v-deep .v-subheader {
    font-weight: 600;
    font-size: 11px;
    color: #718096;
  }
}
</style>
